import React, { useState, useEffect, useCallback } from 'react';
import { X, Save, Beaker, Eye, Edit2 } from 'lucide-react';
import { dataMappingAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface RegimenViewEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  regimen: any | null;
  mode: 'view' | 'edit';
}

const RegimenViewEditModal: React.FC<RegimenViewEditModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  regimen,
  mode: initialMode,
}) => {
  const [mode, setMode] = useState<'view' | 'edit'>(initialMode);
  const [formData, setFormData] = useState({
    regimen_id: '',
    generic_name: '',
    abbreviation1: '',
    abbreviation2: '',
    internal_name1: '',
    internal_name2: '',
    internal_name3: '',
    internal_name4: '',
    internal_name5: '',
    internal_name6: '',
    internal_name7: '',
    internal_name8: '',
    internal_name9: '',
    internal_name10: '',
    med_ids: [] as string[],
  });
  const [availableMedications, setAvailableMedications] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load available medications
  useEffect(() => {
    if (isOpen) {
      const loadMedications = async () => {
        try {
          const medications = await dataMappingAPI.getMedications();
          setAvailableMedications(medications);
        } catch (error) {
          console.error('Error loading medications:', error);
          toast.error('Failed to load medications');
        }
      };
      loadMedications();
    }
  }, [isOpen]);

  // Load regimen data when modal opens or regimen changes
  useEffect(() => {
    if (isOpen && regimen) {
      // Check if it's from mapping table or basic table
      const isMappingFormat = regimen.Regimen_ID || regimen.regimen_id;
      
      if (isMappingFormat) {
        // Mapping format
        setFormData({
          regimen_id: regimen.Regimen_ID || regimen.regimen_id || '',
          generic_name: regimen.Generic_Name || regimen.generic_name || '',
          abbreviation1: regimen.Abbreviation1 || regimen.abbreviation1 || '',
          abbreviation2: regimen.Abbreviation2 || regimen.abbreviation2 || '',
          internal_name1: regimen.Internal_Name1 || regimen.internal_name1 || '',
          internal_name2: regimen.Internal_Name2 || regimen.internal_name2 || '',
          internal_name3: regimen.Internal_Name3 || regimen.internal_name3 || '',
          internal_name4: regimen.Internal_Name4 || regimen.internal_name4 || '',
          internal_name5: regimen.Internal_Name5 || regimen.internal_name5 || '',
          internal_name6: regimen.Internal_Name6 || regimen.internal_name6 || '',
          internal_name7: regimen.Internal_Name7 || regimen.internal_name7 || '',
          internal_name8: regimen.Internal_Name8 || regimen.internal_name8 || '',
          internal_name9: regimen.Internal_Name9 || regimen.internal_name9 || '',
          internal_name10: regimen.Internal_Name10 || regimen.internal_name10 || '',
          med_ids: regimen.Med_IDs && Array.isArray(regimen.Med_IDs) ? regimen.Med_IDs : [],
        });
      } else {
        // Basic format (from regimens table)
        const regimenId = regimen.id ? `R${String(regimen.id).padStart(4, '0')}` : '';
        setFormData({
          regimen_id: regimenId,
          generic_name: regimen.name || '',
          abbreviation1: '',
          abbreviation2: '',
          internal_name1: '',
          internal_name2: '',
          internal_name3: '',
          internal_name4: '',
          internal_name5: '',
          internal_name6: '',
          internal_name7: '',
          internal_name8: '',
          internal_name9: '',
          internal_name10: '',
          med_ids: [],
        });
      }
      setMode(initialMode);
    }
  }, [isOpen, regimen, initialMode]);

  const handleInputChange = (field: string, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleMedicationToggle = (medId: string) => {
    setFormData(prev => {
      const currentIds = prev.med_ids;
      const newIds = currentIds.includes(medId)
        ? currentIds.filter(id => id !== medId)
        : [...currentIds, medId];
      return { ...prev, med_ids: newIds };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Validate regimen_id format if provided
    if (formData.regimen_id.trim() && !formData.regimen_id.match(/^R[0-9]{4}$/)) {
      setErrors({ regimen_id: 'Regimen_ID must be in format R#### (e.g., R0001)' });
      setIsSubmitting(false);
      return;
    }

    try {
      await dataMappingAPI.createRegimenMapping(formData);
      toast.success('Regimen updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error updating regimen:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update regimen';
      toast.error(errorMessage);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !regimen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Beaker className="h-6 w-6 text-[var(--accent)]" />
            <h2 className="text-xl font-semibold text-gray-900">
              {mode === 'view' ? 'View Regimen' : 'Edit Regimen'}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {mode === 'view' && (
              <button
                onClick={() => setMode('edit')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Edit"
              >
                <Edit2 className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {mode === 'view' ? (
            // View Mode
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Regimen_ID</label>
                  <p className="text-sm text-gray-900 font-mono">{formData.regimen_id || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Generic Name</label>
                  <p className="text-sm text-gray-900">{formData.generic_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation 1</label>
                  <p className="text-sm text-gray-900">{formData.abbreviation1 || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Abbreviation 2</label>
                  <p className="text-sm text-gray-900">{formData.abbreviation2 || '-'}</p>
                </div>
              </div>
              
              {formData.med_ids.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Medications</label>
                  <div className="flex flex-wrap gap-2">
                    {formData.med_ids.map(medId => (
                      <span
                        key={medId}
                        className="inline-block px-2 py-1 rounded bg-blue-50 text-blue-700 text-xs font-mono"
                      >
                        {medId}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {(formData.internal_name1 || formData.internal_name2 || formData.internal_name3) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Internal Names</label>
                  <div className="space-y-1">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => {
                      const value = formData[`internal_name${num}` as keyof typeof formData] as string;
                      return value ? (
                        <p key={num} className="text-sm text-gray-900">
                          <span className="font-medium">Internal Name {num}:</span> {value}
                        </p>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Edit Mode
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Regimen_ID */}
              <div>
                <label htmlFor="regimen_id" className="block text-sm font-medium text-gray-700 mb-2">
                  <Beaker className="inline h-4 w-4 mr-1" />
                  Regimen_ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="regimen_id"
                  value={formData.regimen_id}
                  onChange={(e) => handleInputChange('regimen_id', e.target.value.toUpperCase())}
                  placeholder="R0001"
                  pattern="R[0-9]{4}"
                  maxLength={5}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Unique identifier (R + 4 digits, e.g., R0001, R0002).
                </p>
                {errors.regimen_id && (
                  <p className="mt-1 text-xs text-red-500">{errors.regimen_id}</p>
                )}
              </div>

              {/* Generic Name */}
              <div>
                <label htmlFor="generic_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Generic Name
                </label>
                <input
                  type="text"
                  id="generic_name"
                  value={formData.generic_name}
                  onChange={(e) => handleInputChange('generic_name', e.target.value)}
                  placeholder="Common generic regimen name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                />
              </div>

              {/* Abbreviations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="abbreviation1" className="block text-sm font-medium text-gray-700 mb-2">
                    Abbreviation 1
                  </label>
                  <input
                    type="text"
                    id="abbreviation1"
                    value={formData.abbreviation1}
                    onChange={(e) => handleInputChange('abbreviation1', e.target.value.toUpperCase())}
                    placeholder="HRZE, BPaL"
                    maxLength={50}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="abbreviation2" className="block text-sm font-medium text-gray-700 mb-2">
                    Abbreviation 2
                  </label>
                  <input
                    type="text"
                    id="abbreviation2"
                    value={formData.abbreviation2}
                    onChange={(e) => handleInputChange('abbreviation2', e.target.value.toUpperCase())}
                    placeholder="Alternative abbreviation"
                    maxLength={50}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                  />
                </div>
              </div>

              {/* Medications (Multi-select) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Medications
                </label>
                <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto">
                  {availableMedications.length === 0 ? (
                    <p className="text-sm text-gray-500">Loading medications...</p>
                  ) : (
                    <div className="space-y-2">
                      {availableMedications.map((med: any) => {
                        const medId = med.Med_ID || med.med_id || (med.id ? `M${String(med.id).padStart(4, '0')}` : '');
                        const medName = med.Generic_Name || med.generic_name || med.name || medId;
                        const isSelected = formData.med_ids.includes(medId);
                        
                        return (
                          <label
                            key={medId}
                            className="flex items-center space-x-2 p-2 rounded hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleMedicationToggle(medId)}
                              className="rounded border-gray-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                            />
                            <span className="text-sm text-gray-700">
                              <span className="font-mono font-medium">{medId}</span>
                              {medName !== medId && <span className="ml-2 text-gray-600">- {medName}</span>}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Internal Names (collapsible) */}
              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700 mb-4">
                  Internal Names (Optional)
                </summary>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                    <div key={num}>
                      <label htmlFor={`internal_name${num}`} className="block text-sm font-medium text-gray-700 mb-2">
                        Internal Name {num}
                      </label>
                      <input
                        type="text"
                        id={`internal_name${num}`}
                        value={formData[`internal_name${num}` as keyof typeof formData] as string}
                        onChange={(e) => handleInputChange(`internal_name${num}`, e.target.value)}
                        placeholder={`Internal name ${num}`}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                      />
                    </div>
                  ))}
                </div>
              </details>

              {/* Submit buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    if (initialMode === 'view') {
                      setMode('view');
                    } else {
                      onClose();
                    }
                  }}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {initialMode === 'view' ? 'Cancel' : 'Close'}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegimenViewEditModal;
